import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Gamepad2, Trash2, Layers, Shuffle, Zap, ChevronLeft, ChevronRight } from "lucide-react";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { CtaButton } from "@/components/ui/CtaButton";
import { SubjectPicker, GradePicker } from "@/components/CurriculumPicker";
import GenerateLoader from "@/components/ui/GenerateLoader";
import AttachmentPicker from "@/components/quiz/AttachmentPicker";
import MobiHint from "@/components/MobiHint";

const KINDS = [
  { v: "flashcards", l: "Flashcards", icon: Layers, desc: "Front/back study cards." },
  { v: "match", l: "Match the pair", icon: Shuffle, desc: "Pair terms with definitions." },
  { v: "mcq_arcade", l: "MCQ arcade", icon: Zap, desc: "Fast-paced 4-option rounds." },
];

export default function AIGameCreator() {
  const [params] = useSearchParams();
  const initSubjectId = params.get("subjectId") || "";
  const initSubjectName = params.get("subject") || "";
  const initGradeName = params.get("grade") || "";
  const [form, setForm] = useState({
    subject_id: initSubjectId,
    subject: initSubjectName,
    grade_id: "",
    grade: initGradeName,
    topic: "",
    kind: "flashcards",
    count: 8,
    difficulty: "medium",
  });
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(null);
  const [instructions, setInstructions] = useState("");
  const [attachmentText, setAttachmentText] = useState("");

  const load = async () => {
    const url = initSubjectId ? `/ai/game?subject_id=${initSubjectId}` : "/ai/game";
    try { const { data } = await api.get(url); setGames(data.games || []); } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [initSubjectId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject_id) return toast.error("Pick a subject");
    if (!form.topic) return toast.error("Topic is required");
    setLoading(true);
    try {
      const { data } = await api.post("/ai/game/generate", {
        subject: form.subject,
        topic: form.topic,
        grade: form.grade,
        kind: form.kind,
        count: Number(form.count),
        difficulty: form.difficulty,
        subject_id: form.subject_id,
        instructions,
        attachment_text: attachmentText,
      });
      toast.success("Game generated");
      setOpened(data.game);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this game?")) return;
    try { await api.delete(`/ai/game/${id}`); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="game-creator-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ ai game creator</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2">
          <Gamepad2 size={28} /> Make learning fun
        </h1>
        <p className="text-sm text-neutral-600 mt-1">Flashcards, match-the-pair, and MCQ arcades — generated in seconds. 7 credits per game.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 grid sm:grid-cols-2 gap-3">
        <SubjectPicker
          testid="game-subject"
          className="rounded-md"
          value={form.subject_id}
          onChange={(sid, sname, gid, gname) => setForm({ ...form, subject_id: sid, subject: sname, grade_id: gid || form.grade_id, grade: gname || form.grade })}
        />
        <input className={inp} placeholder="Topic *" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="game-topic" />
        <GradePicker
          testid="game-grade"
          className="rounded-md"
          includeNone
          value={form.grade_id}
          onChange={(gid, gname) => setForm({ ...form, grade_id: gid, grade: gname })}
        />
        <SearchableSelect
          testid="game-difficulty"
          placeholder="Difficulty"
          options={[
            { value: "easy", label: "Easy" },
            { value: "medium", label: "Medium" },
            { value: "hard", label: "Hard" },
          ]}
          value={form.difficulty}
          onChange={(v) => setForm({ ...form, difficulty: v })}
        />
        <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3">
          {KINDS.map((k) => (
            <button type="button" key={k.v} onClick={() => setForm({ ...form, kind: k.v })}
              data-testid={`game-kind-${k.v}`}
              className={`text-left p-4 border ${form.kind === k.v ? "border-[#002FA7] ring-2 ring-[#002FA7]" : "border-neutral-300"} hover:border-[#0A0C10]`}>
              <k.icon size={18} className="mb-2 text-[#002FA7]" />
              <div className="font-semibold text-sm">{k.l}</div>
              <div className="text-xs text-neutral-500 mt-1">{k.desc}</div>
            </button>
          ))}
        </div>
        <input className={inp} type="number" min={3} max={20} value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} placeholder="Number of items (3-20)" />
        <textarea className={`${inp} sm:col-span-2`} rows={2} placeholder="Extra instructions for the AI (optional) — e.g. only use examples from the attached chapter" value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="game-instructions" />
        <div className="sm:col-span-2"><AttachmentPicker onChange={setAttachmentText} /></div>
        <CtaButton type="submit" variant="primary" size="lg" disabled={loading} data-testid="game-submit" className="w-full rounded-md">
          <Sparkles size={16} /> {loading ? <GenerateLoader label="Generating game" phrases={["Picking mechanics…", "Writing questions…", "Balancing rounds…"]} /> : "Generate game"}
        </CtaButton>
      </form>

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ your games</div>
        {games.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500">No games yet — create one above.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {games.map((g) => (
              <div key={g.id} className="bg-white border border-neutral-200 p-4" data-testid={`game-card-${g.id}`}>
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">{g.kind.replace("_", " ")}</div>
                <div className="font-semibold text-sm mt-1">{g.title}</div>
                <div className="text-xs text-neutral-500">{g.subject}{g.grade ? ` · ${g.grade}` : ""}</div>
                <div className="flex justify-between mt-3">
                  <button onClick={() => setOpened(g)} className="text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5">Open</button>
                  <button onClick={() => del(g.id)} className="text-xs px-2.5 py-1.5 border border-neutral-200 hover:border-red-500 text-red-500"><Trash2 size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {opened && <GameModal game={opened} onClose={() => setOpened(null)} />}

      <MobiHint
        label="Ask Mobi for game ideas"
        prompt={form.topic ? `Suggest an interactive game format for teaching "${form.topic}" (${form.subject || "any subject"}) to my students.` : "What kind of game works best for teaching a new concept?"}
      />
    </div>
  );
}

function GameModal({ game, onClose }) {
  const data = game.data || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ {game.kind.replace("_", " ")}</div>
            <h2 className="font-display font-bold text-2xl tracking-tight">{game.title}</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500">✕</button>
        </div>
        {game.kind === "flashcards" && <FlashcardsPlayer cards={data.cards || []} />}
        {game.kind === "match" && <MatchView pairs={data.pairs || []} />}
        {game.kind === "mcq_arcade" && <ArcadeView rounds={data.rounds || []} />}
      </div>
    </div>
  );
}

function FlashcardsPlayer({ cards }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  if (!cards.length) return <div className="text-sm text-neutral-500">No cards.</div>;
  const c = cards[idx];
  return (
    <div className="space-y-4">
      <div onClick={() => setFlipped(!flipped)} className="bg-[#0A0C10] text-white p-12 min-h-[260px] flex items-center justify-center text-center cursor-pointer" data-testid="flashcard">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">{flipped ? "Back" : "Front"}</div>
          <div className="font-display text-2xl">{flipped ? c.back : c.front}</div>
          {!flipped && c.hint && <div className="text-xs text-neutral-400 mt-3 italic">Hint: {c.hint}</div>}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <button disabled={idx === 0} onClick={() => { setIdx(idx - 1); setFlipped(false); }} className="border border-neutral-300 px-3 py-1.5 text-sm disabled:opacity-30 inline-flex items-center gap-1"><ChevronLeft size={14} /> Prev</button>
        <div className="text-xs text-neutral-500">{idx + 1} / {cards.length}</div>
        <button disabled={idx === cards.length - 1} onClick={() => { setIdx(idx + 1); setFlipped(false); }} className="border border-neutral-300 px-3 py-1.5 text-sm disabled:opacity-30 inline-flex items-center gap-1">Next <ChevronRight size={14} /></button>
      </div>
    </div>
  );
}

function MatchView({ pairs }) {
  if (!pairs.length) return <div className="text-sm text-neutral-500">No pairs.</div>;
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1">Terms</div>
        {pairs.map((p, i) => <div key={i} className="border border-neutral-300 p-3 text-sm">{p.left}</div>)}
      </div>
      <div className="space-y-2">
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1">Definitions</div>
        {pairs.map((p, i) => <div key={i} className="border border-neutral-300 p-3 text-sm">{p.right}</div>)}
      </div>
    </div>
  );
}

function ArcadeView({ rounds }) {
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  if (!rounds.length) return <div className="text-sm text-neutral-500">No rounds.</div>;
  const r = rounds[idx];
  const reset = () => { setPicked(null); };
  return (
    <div className="space-y-4">
      <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">Round {idx + 1} / {rounds.length}</div>
      <div className="font-display font-bold text-xl">{r.question}</div>
      <div className="grid grid-cols-2 gap-2">
        {(r.options || []).map((opt, i) => {
          const isAnswer = i === r.correct_index;
          const isPicked = picked === i;
          const cls = picked == null
            ? "border border-neutral-300 hover:border-[#0A0C10]"
            : isAnswer ? "bg-green-500 text-white border border-green-500"
              : isPicked ? "bg-red-500 text-white border border-red-500" : "border border-neutral-300 opacity-60";
          return (
            <button key={i} disabled={picked != null} onClick={() => setPicked(i)} data-testid={`arcade-opt-${i}`} className={`text-left p-3 text-sm ${cls}`}>{opt}</button>
          );
        })}
      </div>
      {picked != null && r.explanation && <div className="text-xs text-neutral-600 italic">{r.explanation}</div>}
      <div className="flex justify-end">
        <button disabled={idx === rounds.length - 1} onClick={() => { setIdx(idx + 1); reset(); }} className="border border-neutral-300 px-3 py-1.5 text-sm disabled:opacity-30">Next round</button>
      </div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
